import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  OnInit,
  signal,
  untracked,
} from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { LeafletModule } from '@bluehalo/ngx-leaflet';
import * as L from 'leaflet';

import { createBasemapLayer } from '../../shared/utils/leaflet-basemap';
import { ENV_CONFIG } from '../../core/env-config';
import { relativeTime } from '../../shared/utils/relative-time';
import { loadMarkerCluster } from '../../shared/utils/leaflet-marker-cluster';
import { createClusterIcon, createHaloIcon, createPinIcon, STATUS_SEVERITY_ORDER, worstStatus } from './utils/marker-icons';
import { GridPage } from '../../shared/components/grid/grid-page';
import { Select, SelectOption } from '../../shared/components/forms/select';
import { GridStatusColor } from '../../shared/components/grid/models/grid.model';
import { LabelBadge, LabelBadgeDot } from '../../shared/components/label-badge/label-badge';
import { Icon } from '../../shared/components/icon/icon';
import { ViewportService } from '../../core/services/viewport.service';
import { ToastService } from '../../shared/services/toast-service';
import { FleetMapService } from './services/fleet-map.service';
import { FleetService } from './services/fleet.service';
import { Spinner } from '../../shared/components/spinner/spinner';
import {
  FleetDensity,
  FleetFilter,
  FleetMapPin,
  FleetStatus,
  FleetStatusCount,
  FleetTile,
  FleetTileDetail,
  MonitoringTab,
} from './models/monitoring.model';

/** Design's tiles-per-page; drives the server page size. */
const TILES_PER_PAGE = 40;

/** Per-user density preference, persisted across sessions. */
const DENSITY_STORAGE_KEY = 'monitoring.fleet.density';

/** Snapshot poll interval — matches the rail caption's "refresh every 60 s". */
const SNAPSHOT_REFRESH_MS = 60_000;

/** Fleet status → status-vocabulary colour (dot/tint). Grey states map to neutral. */
const STATUS_COLOR: Record<FleetStatus, GridStatusColor> = {
  ok: 'green',
  warning: 'yellow',
  error: 'red',
  offline: 'neutral',
  shutdown: 'neutral',
};

/** The order legend / summary breakdowns are shown in. */
const LEGEND_ORDER: FleetStatus[] = ['ok', 'warning', 'error', 'offline', 'shutdown'];

/** The `clusterclick`/`clustermouseover` event shape — untyped by
 *  `@types/leaflet.markercluster` beyond the generic `LeafletEvent`. */
interface ClusterEvent extends L.LeafletEvent {
  layer: L.MarkerCluster;
}

/**
 * Monitoring — fleet status matrix.
 *
 * A purpose-built page (NOT the universal grid, whose row/cell/table layout
 * doesn't map to a tile matrix). It reuses the grid page shell, the `Select`
 * dropdown, and the status colour vocabulary; everything else — hero card, tile
 * grid, legend, map, right rail — is local markup.
 *
 * Scope: Fleet and Map tabs are implemented; Alerts and Reports show a
 * "not yet supported" placeholder.
 *
 * Data sources — three calls total for the whole page, each fetched lazily
 * where it makes sense:
 *  - `GET /monitoring/fleet` (`FleetService.getFleetTiles`, `load()`) — the
 *    ONLY call needed to render the grid, Compact or Detailed. No per-tile
 *    follow-ups for battery/signal/snapshot; those arrive inline.
 *  - `GET /monitoring/{clientId}/detail` (`FleetService.getDeviceDetail`,
 *    `loadDetail()`) — fired once per selection, whether the device came
 *    from a grid tile or a map pin. Self-contained: doesn't require the
 *    device to be on the currently-loaded grid page.
 *  - `GET /monitoring/fleet-map` (`FleetMapService`, `loadFleetMapPins()`)
 *    — whole-fleet pins for the Map tab, fetched only once that tab is
 *    actually opened (see `onTab`).
 *
 * Known gaps in the live data (flag to product/frontend before relying on
 * these further):
 *  1. `FleetTile.label` currently always equals `clientId` — no human
 *     display-name concept exists on the device yet. Same for
 *     `FleetTile.model`, which is really the device's internal MQTT-routing
 *     `type` field, not a curated hardware-model vocabulary.
 *  2. `signal` (on both `/fleet` and `/detail`) is a raw device-reported
 *     number, NOT the `good/fair/poor/none` quality enum originally specced
 *     — no documented units/thresholds exist anywhere to bucket it safely,
 *     so it's rendered as-is. Revisit once a real scale is confirmed.
 *  3. `error`/`shutdown` statuses and `FleetTileDetail.alerts` are real in
 *     both contracts but structurally unreachable today — no device-reported
 *     fault/alert signal exists in the backend yet. Rendering already
 *     handles both; they'll just start appearing once the backend adds them.
 *  4. There is no site filter — `/monitoring/fleet` has no site concept and
 *     omits the field entirely (not sent as null). The Fleet tab's site
 *     filter/display was removed rather than shipped against absent data.
 *  5. The rail's snapshot image is currently the same thumbnail asset as
 *     the grid's — no separate higher-resolution rail image exists yet,
 *     despite the rail rendering it at up to 252px.
 *  6. [RESOLVED] `openTile()` navigates by clientId alone — the device
 *     overview page now fetches its own data via
 *     `GET /api/v1/device/{clientId}` (`ProvisioningService.
 *     getDeviceByClientId`) instead of requiring the full device DTO via
 *     router navigation state, so this page no longer needs to carry one.
 *  7. No content-freshness metric/definition; no alerts data model beyond
 *     the always-empty placeholder in `FleetTileDetail.alerts`.
 */
@Component({
  selector: 'app-monitoring',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoDirective, FormsModule, GridPage, Select, LabelBadge, LeafletModule, NgTemplateOutlet, Icon, Spinner],
  templateUrl: './monitoring.component.html',
  styleUrl: './monitoring.component.css',
})
export class MonitoringComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly fleetService = inject(FleetService);
  private readonly env = inject(ENV_CONFIG);
  private readonly fleetMapService = inject(FleetMapService);
  private readonly toastService = inject(ToastService);
  private readonly transloco = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);

  /** Below lg the right rail renders as a bottom sheet when a device is selected. */
  protected readonly compactDetail = inject(ViewportService).isCompactDetail;

  /** Namespaced translate helper. */
  private readonly t = (key: string, params?: Record<string, unknown>): string =>
    this.transloco.translate(`monitoring.${key}`, params);

  // ── State ───────────────────────────────────────────────────────────────
  readonly tab = signal<MonitoringTab>('fleet');
  readonly isLoading = signal(false);

  readonly page = signal(0);
  readonly size = signal(TILES_PER_PAGE);
  readonly totalElements = signal(0);
  readonly totalPages = signal(0);

  readonly statusFilter = signal<FleetFilter>('all');
  readonly model = signal<string>('all');
  readonly selectedTileId = signal<string | null>(null);
  readonly density = signal<FleetDensity>(this.loadDensityPref());

  /** The current page's tiles, exactly as `GET /monitoring/fleet` returns
   *  them — no client-side mapping/derivation needed any more. */
  readonly allTiles = signal<FleetTile[]>([]);

  /** Whole-fleet pins for the map, from `GET .../monitoring/fleet-map` —
   *  NOT page-local like `allTiles`. See `loadFleetMapPins`. */
  private readonly fleetMapPins = signal<FleetMapPin[]>([]);
  readonly isLoadingFleetMap = signal(false);

  /** The selected device's rail data, from `GET .../{clientId}/detail`.
   *  `null` while nothing is selected OR while a selection's detail is
   *  still loading — `isLoadingDetail` disambiguates the two for the
   *  template. See `loadDetail`. */
  readonly selectedTileDetail = signal<FleetTileDetail | null>(null);
  readonly isLoadingDetail = signal(false);

  /**
   * Whatever's known about the current selection synchronously, before (or
   * without) the async detail call — the matching page-local `FleetTile` if
   * selected from the grid, else the matching `FleetMapPin` if selected from
   * the map, else just the bare clientId with an unknown status. Drives the
   * rail header (name/status chip), which `/detail` itself doesn't carry.
   */
  readonly selectedSummary = computed<{ clientId: string; label: string; status: FleetStatus | null } | null>(() => {
    const id = this.selectedTileId();
    if (id === null) return null;
    const tile = this.allTiles().find((t) => t.clientId === id);
    if (tile) return { clientId: tile.clientId, label: tile.label, status: tile.status };
    const pin = this.fleetMapPins().find((p) => p.label === id);
    if (pin) return { clientId: pin.label, label: pin.label, status: pin.status };
    return { clientId: id, label: id, status: null };
  });

  /**
   * Snapshot display orientation, keyed by `imageUrl` — `3/4` (portrait) or
   * `4/3` (landscape), per `design_handoff_snapshot/README.md`. There is no
   * orientation field on `FleetSnapshot`/the device today, so this is
   * detected client-side from the loaded image's own
   * `naturalWidth`/`naturalHeight` (see `onSnapshotLoad`) rather than bound
   * to backend metadata — which also means a capture's actual ratio can
   * never disagree with "declared" orientation (there being none), so the
   * README's mismatch fallback doesn't apply here.
   * Until an image has loaded once, `snapshotAspectRatio` falls back to
   * `4/3` — the loading/failed frame can visibly resize the first time a
   * portrait capture lands; a real orientation field on the API would
   * remove that gap.
   */
  private readonly snapshotOrientation = signal<ReadonlyMap<string, 'portrait' | 'landscape'>>(new Map());

  /** Distinct model options derived from the loaded page. */
  readonly modelOptions = computed<SelectOption[]>(() => {
    const models = Array.from(new Set(this.allTiles().map((t) => t.model))).sort((a, b) =>
      a.localeCompare(b),
    );
    return [{ value: 'all', label: this.t('filters.allModels') }, ...models.map((m) => ({ value: m, label: m }))];
  });

  // ── Loading states (same contract as the shared grid) ───────────────────

  /** Cold load: loading with nothing on screen → skeleton cards. */
  readonly showSkeleton = computed(() => this.isLoading() && this.allTiles().length === 0);
  /** User-initiated refetch (pagination): keep the cards, dimmed, until fresh
   *  data lands. The 60s poll loads silently, so it never trips this. */
  readonly isRefetching = computed(() => this.isLoading() && this.allTiles().length > 0);
  /** Bone-card count: fills ~2 rows at the widest breakpoint; no need to
   *  mirror the 40-tile page. */
  readonly skeletonTiles = Array.from({ length: 10 });

  /** Browser-fetch state per snapshot `<img>`, keyed by imageUrl like
   *  `snapshotOrientation`: a URL is "fetching" from first render until its
   *  load or error event — backend `snapshot.status` can't see this phase. */
  private readonly loadedSnapshots = signal<ReadonlySet<string>>(new Set());
  private readonly failedSnapshots = signal<ReadonlySet<string>>(new Set());

  /** Tiles after the status/model filters (page-local — see class doc). */
  readonly tiles = computed<FleetTile[]>(() => {
    const status = this.statusFilter();
    const model = this.model();
    return this.allTiles().filter((tile) => {
      if (model !== 'all' && tile.model !== model) return false;
      if (status === 'attention') return tile.status === 'warning' || tile.status === 'error' || tile.status === 'offline';
      if (status === 'offline') return tile.status === 'offline' || tile.status === 'shutdown';
      return true;
    });
  });

  // ── Aggregates (page-local; honest placeholders where no data) ────────────

  /** Status counts over the loaded page — feeds the legend and summary rail. */
  readonly legend = computed<FleetStatusCount[]>(() => {
    const counts = new Map<FleetStatus, number>();
    for (const tile of this.allTiles()) counts.set(tile.status, (counts.get(tile.status) ?? 0) + 1);
    return LEGEND_ORDER.map((status) => ({
      status,
      label: this.t(`legend.${status === 'shutdown' ? 'shutdown' : status}`),
      count: counts.get(status) ?? 0,
      color: STATUS_COLOR[status],
    }));
  });

  /** Toolbar legend — the design shows only the four primary statuses in the
   *  control row; the rail's summary keeps the full breakdown incl. shut down. */
  readonly toolbarLegend = computed<FleetStatusCount[]>(() =>
    this.legend().filter((l) => l.status !== 'shutdown'),
  );

  /** Displays reporting warning/error/offline on the loaded page. */
  readonly attentionCount = computed<number>(
    () => this.allTiles().filter((t) => t.status === 'warning' || t.status === 'error' || t.status === 'offline').length,
  );

  readonly offlineCount = computed<number>(
    () => this.allTiles().filter((t) => t.status === 'offline').length,
  );

  readonly reachableCount = computed<number>(
    () => this.allTiles().filter((t) => t.status !== 'offline' && t.status !== 'shutdown').length,
  );

  /**
   * Fleet health % over the loaded page (ok + shutdown counted as "not in
   * trouble"). Page-local; a real figure needs a fleet-wide aggregates
   * endpoint.
   */
  readonly healthPct = computed<number | null>(() => {
    const total = this.allTiles().length;
    if (total === 0) return null;
    const healthy = this.allTiles().filter((t) => t.status === 'ok' || t.status === 'shutdown').length;
    return Math.round((healthy / total) * 100);
  });

  // ── Fleet map ──────────────────────────────────────────────────────────────
  // Teardrop pins + clustering per README-2a-map-tab.md. See marker-icons.ts
  // for the pin/cluster SVG builders.
  mapOptions: L.MapOptions = {
    layers: [createBasemapLayer(this.env.cartoApiKey)],
    zoom: 12,
    center: L.latLng(0, 0),
    scrollWheelZoom: false,
  };

  /**
   * A signal, not a plain field — this component is OnPush, and the pin
   * effect below runs from an HTTP response, not a click/event inside this
   * view, so a plain field mutated there never marks the OnPush view dirty
   * (a real bug this page had: markers built correctly but invisible until
   * an unrelated click forced a check). Reading a signal in the template
   * wires it into Angular's reactive graph directly.
   *
   * The array itself is set exactly ONCE, to `[clusterGroup]` — the whole
   * point of clustering is one long-lived `MarkerClusterGroup` instance;
   * pin updates mutate its contents imperatively (`clearLayers`/
   * `addLayers`), they never replace this array or trigger the diffing
   * `[leafletLayers]` does on array identity.
   */
  readonly mapLayers = signal<L.Layer[]>([]);

  /**
   * The single cluster group backing the whole map. Cluster click/hover
   * wiring lives on this instance — `clusterclick`/`clustermouseover`/
   * `clustermouseout` are events the plugin fires on the *group*, there is
   * no separate Angular-visible object per cluster to bind handlers to.
   *
   * Created lazily (see `clusterGroupReady`, built in the constructor) —
   * NOT as a synchronous class-field initializer any more. Constructing it
   * needs `L.markerClusterGroup` to already exist, which depends on
   * `leaflet.markercluster` having loaded; see `loadMarkerCluster`'s doc
   * comment for why that's a dynamic import now, not a static one.
   */
  private clusterGroup!: L.MarkerClusterGroup;
  private readonly clusterGroupReady: Promise<void>;

  /** Pin data keyed by Leaflet marker instance — recovers which device(s)
   *  a marker/cluster represents without a second array to keep in sync. */
  private readonly pinByMarker = new Map<L.Marker, FleetMapPin>();
  /** Keyed by `label` (clientId) — the fleet-map pin's own `id` is a
   *  different numeric id space than `FleetTile.clientId`. See `FleetMapPin` doc. */
  private readonly markersById = new Map<string, L.Marker>();
  /**
   * The selection ring, as its OWN separate overlay marker — not a
   * resized/re-ringed variant of the pin's own icon. Swapping (or even
   * replacing) a pin's own marker/icon for its "selected" look proved
   * unreliable in practice once combined with moving it between the
   * cluster group and the map directly: a deselected pin could keep
   * showing its old ringed icon indefinitely, and neither reordering the
   * icon-swap around the layer move nor recreating the marker instance
   * fixed it. A wholly separate, purely decorative ring layer — added at
   * the selected pin's coordinates, removed on deselect — sidesteps the
   * whole question of when Leaflet does or doesn't rebuild a marker's
   * icon DOM, because the pin's own icon is simply never touched.
   *
   * Deliberately decoupled from `selectedTileId`/the detail rail: shown on
   * a pin click, cleared by ANY click that isn't on that pin (see
   * `onDocumentClick`) — not by the rail closing. They used to be the same
   * signal; that coupling is what kept leaving a stale ring, so this is
   * intentionally its own thing now, not synchronized with drawer state.
   */
  private haloMarker: L.Marker | null = null;
  private haloedPinLabel: string | null = null;

  /**
   * Members of the currently hovered cluster. Tracked for correctness (and
   * for any future combined view), but NOT wired into the Fleet tab's tile
   * grid today — Fleet and Map are separate, mutually exclusive tabs here
   * (unlike the design's assumption that both are visible together), so a
   * live hover-highlight on tiles the user can't currently see would never
   * actually be witnessed happening.
   */
  readonly hoverIds = signal<string[] | null>(null);

  private leafletMap: L.Map | null = null;

  constructor() {
    this.clusterGroupReady = loadMarkerCluster(L).then(() => {
      this.clusterGroup = L.markerClusterGroup({
        maxClusterRadius: 26,
        showCoverageOnHover: false,
        zoomToBoundsOnClick: false, // driven manually — see onClusterClick
        spiderfyDistanceMultiplier: 1.4,
        iconCreateFunction: (cluster) => {
          const pins = cluster
            .getAllChildMarkers()
            .map((m) => this.pinByMarker.get(m))
            .filter((p): p is FleetMapPin => !!p);
          cluster.bindTooltip(this.clusterTooltipHtml(pins), {
            direction: 'top',
            offset: [0, -46],
            className: 'pc-cluster-tooltip',
          });
          return createClusterIcon(pins.length, worstStatus(pins.map((p) => p.status)));
        },
      });
      this.clusterGroup.on('clusterclick', (e) => this.onClusterClick(e as ClusterEvent));
      this.clusterGroup.on('clustermouseover', (e) => this.onClusterHover(e as ClusterEvent));
      this.clusterGroup.on('clustermouseout', () => this.hoverIds.set(null));
      this.mapLayers.set([this.clusterGroup]);
    });

    // Rebuild pins whenever the fleet-wide pin set changes. Awaits cluster
    // group readiness first — pins can arrive (Map tab opened, fetch
    // resolves) before the dynamic plugin import settles, though in
    // practice that import resolves within a microtask or two since it's
    // already part of the bundle, not a real network fetch.
    effect(() => {
      const pins = this.fleetMapPins();
      void this.buildMapMarkers(pins);
    });
    // NOTE: the rail's own selection (selectedTileId) is driven directly by
    // selectTile/selectPin/clearSelection, not a reactive effect — see
    // those methods. The halo ring below is intentionally a SEPARATE,
    // independent piece of state, not wired to selectedTileId at all.

    // "Click anywhere besides the haloed pin clears the halo." Split in
    // two, deliberately NOT one blanket capture-phase listener (that was
    // the first attempt — it fires on the way DOWN to the target, i.e.
    // BEFORE Leaflet's own marker-click handling has a chance to run or
    // stop propagation, which made pin-vs-background clicks unreliable to
    // tell apart here):
    //  - Clicks INSIDE the map are Leaflet's own territory: a pin click
    //    (marker.on('click', ...) in createMarker) shows the halo there
    //    (clearing any previous one first); a background click
    //    (map.on('click', ...) in onMapReady) clears it. Both are normal
    //    Leaflet event bindings, not something this listener needs to
    //    duplicate.
    //  - Clicks OUTSIDE the map entirely (the rail, anything else in the
    //    app) aren't Leaflet's to handle at all — this bubble-phase
    //    document listener only checks "was this click inside the map
    //    container," and clears the halo if it wasn't. Bubble phase (not
    //    capture) so it runs after, never in front of, Leaflet's own
    //    handling of clicks that DID land inside the map.
    const onDocumentClick = (e: MouseEvent) => this.onDocumentClickOutsideMap(e);
    document.addEventListener('click', onDocumentClick);
    this.destroyRef.onDestroy(() => document.removeEventListener('click', onDocumentClick));
  }

  private onDocumentClickOutsideMap(e: MouseEvent): void {
    if (!this.haloedPinLabel || !this.leafletMap) return;
    const mapContainer = this.leafletMap.getContainer();
    if (e.target instanceof Node && mapContainer.contains(e.target)) return; // inside the map — Leaflet's own handlers own this
    this.clearHalo();
  }

  onMapReady(map: L.Map): void {
    this.leafletMap = map;
    // Clicking the base map background clears both the rail selection and
    // the halo — belt-and-suspenders alongside `onDocumentClick`, which
    // should already catch this via bubbling, but explicit here since it's
    // cheap and removes any doubt about whether Leaflet's own map click
    // handling lets the native event bubble all the way to `document`.
    map.on('click', () => {
      this.clearSelection();
      this.clearHalo();
    });
    this.fitMapToPins();
  }

  private async buildMapMarkers(pins: FleetMapPin[]): Promise<void> {
    await this.clusterGroupReady;
    this.clusterGroup.clearLayers();
    this.pinByMarker.clear();
    this.markersById.clear();
    this.clearHalo();

    const markers = pins.map((pin) => this.createMarker(pin));
    this.clusterGroup.addLayers(markers);

    // untracked(): this method runs inside the pins-loading effect() (see
    // the constructor). Reading selectedTileId() directly here would make
    // Angular treat it as a dependency of THAT effect too — since effects
    // track every signal read during their execution, not just the one you
    // meant to depend on — so every selection change (every pin click)
    // would re-run this whole method, rebuilding the cluster group AND
    // re-fitting bounds to every pin. That was the actual cause of the map
    // silently resetting to the whole-fleet view on every pin click,
    // regardless of cluster/zoom state. untracked() reads the current
    // value without registering it as a dependency — this call only needs
    // to reflect whatever's currently selected when pins first load, not
    // to re-run every time selection changes afterward (selectTile/
    // selectPin/clearSelection already call applySelection directly).
    this.applySelection(untracked(() => this.selectedTileId()));
    this.fitMapToPins();
  }

  /**
   * Builds one marker. Its icon is always the plain, unselected pin —
   * "selected" is shown entirely via the separate `haloMarker` overlay
   * (see its doc comment for why), never by changing this marker's own
   * icon. Always updates `pinByMarker`/`markersById` — callers don't need to.
   */
  private createMarker(pin: FleetMapPin): L.Marker {
    const marker = L.marker([pin.lat, pin.lng], { icon: createPinIcon(pin.status) }).bindPopup(
      () => this.calloutHtml(pin),
      // No extra offset here — the pin's own icon already defines
      // popupAnchor (see createPinDivIcon in pin-shape.ts); adding another
      // offset on top of that stacked additively, pushing the callout
      // noticeably higher than intended.
      // autoClose:false — Leaflet's default closes any other open popup
      // whenever a new one opens. With that on, hovering pin B while pin A
      // is selected (and its callout open) would silently close A's
      // callout; our own hover/selection logic never asked for that and
      // has no way to know it happened, so stopping the hover over B left
      // NEITHER callout open even though A was still selected underneath.
      { closeButton: false, minWidth: 190, autoClose: false },
    );

    // No separate hover tooltip any more — the callout itself is now both:
    // shown on hover (closes on mouseout, unless selected), and stays open
    // while selected regardless of hover. `bindPopup` wires its OWN click
    // listener that TOGGLES the popup open/closed based on current popup
    // visibility — that fights with "stays open while selected" (e.g. hover
    // already showed it, then a selecting click would toggle it CLOSED
    // instead of opening/keeping it open). Remove that default wiring and
    // drive open/close entirely from our own hover + selection state below.
    marker.off('click', (marker as unknown as { _openPopup: L.LeafletEventHandlerFn })._openPopup);

    let isHovering = false;
    const syncPopup = () => {
      if (isHovering || this.selectedTileId() === pin.label) marker.openPopup();
      else marker.closePopup();
    };
    marker.on('mouseover', () => {
      isHovering = true;
      syncPopup();
    });
    marker.on('mouseout', () => {
      isHovering = false;
      syncPopup();
    });
    marker.on('click', () => {
      // Halo follows the SAME toggle as the rail, not a click on any pin —
      // clicking the same pin repeatedly used to open/close the rail while
      // the halo just kept re-showing itself every time regardless, so the
      // two visibly fell out of sync after the second click.
      if (this.selectPin(pin)) this.showHalo(pin);
      else this.clearHalo();
      syncPopup();
    });
    this.pinByMarker.set(marker, pin);
    this.markersById.set(pin.label, marker);
    return marker;
  }

  /** Clicking a pin selects it by clientId — works identically to clicking a
   *  grid tile, whether or not the device is on the currently loaded grid
   *  page, since `loadDetail` doesn't depend on page-local data. Returns
   *  whether the pin ended up selected (vs. toggled off). */
  private selectPin(pin: FleetMapPin): boolean {
    const next = this.selectedTileId() === pin.label ? null : pin.label;
    this.selectedTileId.set(next);
    this.applySelection(next);
    if (next) this.loadDetail(next);
    return next !== null;
  }

  /**
   * Selection visuals: every pin but the selected one dims to 0.4 opacity.
   *
   * NOT implemented (removed after causing a real regression): pulling the
   * selected marker out of the cluster group and onto the map directly so
   * it stays visible/frontmost at any zoom. `clusterGroup.removeLayer`/
   * `addLayer` on a live cluster group triggers the plugin's own internal
   * recompute of its cluster tree, and doing that on every pin click was
   * the apparent cause of the map's view silently resetting/zooming back
   * out right after a cluster-click zoom-in — clicking a pin was mutating
   * cluster membership as a side effect of selecting it. The halo overlay
   * is the only "this one is selected" visual now; it doesn't require
   * touching cluster membership at all.
   */
  private applySelection(selectedId: string | null): void {
    for (const [id, marker] of this.markersById) {
      marker.setOpacity(selectedId === null || id === selectedId ? 1 : 0.4);
    }
  }

  /** Shows the halo overlay at `pin`'s location, replacing any previous
   *  one. See the `haloMarker` field doc comment for why this is a
   *  separate layer rather than a change to the pin's own icon. */
  private showHalo(pin: FleetMapPin): void {
    this.clearHalo();
    this.haloMarker = L.marker([pin.lat, pin.lng], {
      icon: createHaloIcon(),
      zIndexOffset: -1000, // behind every normal pin (offset 0) or pulled-out pin
      interactive: false, // never intercepts clicks meant for the pin/map under it
    });
    this.leafletMap?.addLayer(this.haloMarker);
    this.haloedPinLabel = pin.label;
  }

  private clearHalo(): void {
    if (this.haloMarker) {
      this.leafletMap?.removeLayer(this.haloMarker);
      this.haloMarker = null;
    }
    this.haloedPinLabel = null;
  }

  /**
   * Zoom to fit the cluster's bounds; once that stops helping (the fit zoom
   * is no longer greater than the current one, or the map is already at
   * max zoom), spiderfy instead. `zoomToBoundsOnClick` is off on the group
   * so this is the only zoom/spiderfy behaviour — never the plugin's default.
   */
  private onClusterClick(e: ClusterEvent): void {
    const map = this.leafletMap;
    if (!map) return;
    const bounds = e.layer.getBounds();
    const targetZoom = map.getBoundsZoom(bounds, false, L.point(40, 40));
    if (targetZoom > map.getZoom() && map.getZoom() < map.getMaxZoom()) {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: map.getMaxZoom() });
    } else {
      e.layer.spiderfy();
    }
  }

  private onClusterHover(e: ClusterEvent): void {
    const ids = e.layer
      .getAllChildMarkers()
      .map((m) => this.pinByMarker.get(m)?.label)
      .filter((id): id is string => !!id);
    this.hoverIds.set(ids);
  }

  /**
   * Pin callout (click popup) content. The design spec's 3-line callout
   * (name / code·site·status / last-sync+battery) assumes device fields
   * `FleetMapPin` doesn't carry — it's deliberately lean (id/label/status/
   * lat/lng only, see its doc comment), so `label` already stands in for
   * "name" (no separate human name or code exists — same stopgap as
   * `FleetTile.label`), there's no site, and battery/lastSync live only on
   * the per-device `/detail` endpoint (fetched into the rail on click, not
   * duplicated here just for the map). Rendered with what's actually
   * available: status dot + label, then the status word.
   */
  private calloutHtml(pin: FleetMapPin): string {
    return `
      <div style="display:flex;flex-direction:column;gap:4px;font-family:'Open Sans',sans-serif;">
        <div style="display:flex;align-items:center;gap:6px;font-size:13px;font-weight:700;color:#1A1A1A;">
          <span style="width:8px;height:8px;border-radius:999px;background:${this.dotHexColor(pin.status)};flex:none;"></span>
          ${this.escapeHtml(pin.label)}
        </div>
        <div style="font-size:11px;color:#4D4D4D;">${this.escapeHtml(this.statusLabel(pin.status))}</div>
      </div>`;
  }

  /** Cluster hover tooltip content: "<n> displays" (no site breakdown — the
   *  map's pin data has no site field, see `FleetMapPin` doc), then one row
   *  per status present, in severity order. */
  private clusterTooltipHtml(pins: FleetMapPin[]): string {
    const counts = new Map<FleetStatus, number>();
    for (const pin of pins) counts.set(pin.status, (counts.get(pin.status) ?? 0) + 1);
    const rows = STATUS_SEVERITY_ORDER.filter((status) => counts.has(status))
      .map(
        (status) => `
        <div style="display:flex;align-items:center;gap:6px;font-size:11px;color:#4D4D4D;">
          <span style="width:7px;height:7px;border-radius:999px;background:${this.dotHexColor(status)};flex:none;"></span>
          ${counts.get(status)} ${this.escapeHtml(this.statusLabel(status).toLowerCase())}
        </div>`,
      )
      .join('');
    return `
      <div style="display:flex;flex-direction:column;gap:4px;font-family:'Open Sans',sans-serif;">
        <div style="font-size:12px;font-weight:700;color:#1A1A1A;">${this.t('map.clusterDisplays', { count: pins.length })}</div>
        ${rows}
      </div>`;
  }

  /** Hex/rgb dot colour for a status — the 500-level icon-grade hues,
   *  matching the legend/tile dots (`dotClass`) and LabelBadge (NOT the
   *  pin-fill swap used for the Warning pin/cluster body, which is a
   *  different colour reserved for large fills only). Literal values, not
   *  var() references — this lands in Leaflet-injected HTML strings. */
  private dotHexColor(status: FleetStatus): string {
    switch (status) {
      case 'ok':
        return 'rgb(0,174,0)';
      case 'warning':
        return 'rgb(216,201,0)';
      case 'error':
        return 'rgb(217,45,32)';
      default:
        return '#999';
    }
  }

  /** Popup/tooltip content is raw HTML strings (Leaflet, not Angular
   *  templates) — escape anything that isn't a fixed literal. */
  private escapeHtml(value: string): string {
    const div = document.createElement('div');
    div.textContent = value;
    return div.innerHTML;
  }

  private fitMapToPins(): void {
    if (!this.leafletMap || this.markersById.size === 0) return;
    const bounds = L.latLngBounds([...this.markersById.values()].map((m) => m.getLatLng()));
    this.leafletMap.fitBounds(bounds, { padding: [24, 24] });
    // Tiles render grey if resized before this; harmless if called early too.
    setTimeout(() => this.leafletMap?.invalidateSize(), 0);
  }

  private loadDensityPref(): FleetDensity {
    try {
      const stored = localStorage.getItem(DENSITY_STORAGE_KEY);
      return stored === 'compact' || stored === 'detailed' ? stored : 'detailed';
    } catch {
      return 'detailed';
    }
  }

  onDensity(next: FleetDensity): void {
    this.density.set(next);
    try {
      localStorage.setItem(DENSITY_STORAGE_KEY, next);
    } catch {
      // Storage unavailable (private mode, quota) — density just won't persist.
    }
    // No fetch needed: /monitoring/fleet already returns everything both
    // densities render, so switching density is a pure layout change.
  }

  ngOnInit(): void {
    this.load();
    // Snapshot refresh, per the rail caption's "refresh every 60 s" — swaps
    // `src` on the existing tracked-by-clientId tiles/img elements (see the
    // `@for`/`snapshotView` template) without tearing down the frame, so the
    // grid/rail layout doesn't jump. Only while the Fleet tab (the only tab
    // with tile/rail snapshots) is open and nothing else is already loading.
    const intervalId = setInterval(() => {
      if (this.tab() !== 'fleet' || this.isLoading()) return;
      // Silent: a background refresh must not dim the grid or show skeletons.
      this.load(true);
      const selected = this.selectedTileId();
      if (selected) this.loadDetail(selected);
    }, SNAPSHOT_REFRESH_MS);
    this.destroyRef.onDestroy(() => clearInterval(intervalId));
  }

  /**
   * Whole-fleet pins for the map, from `GET .../monitoring/fleet-map`.
   * Fetched lazily — only once the Map tab is actually opened (see `onTab`),
   * not on page load, since the default view is the Fleet grid and there's
   * no route change on tab switch to hang a resolver off. `hasLoadedFleetMap`
   * guards against refetching on every switch back to the tab; nothing here
   * currently invalidates it (no polling, no manual refresh yet).
   *
   * No guaranteed server-side order — sorted by `label` for a stable pin
   * z-order / predictable tooltip reading order. Devices with no
   * provisioning or no parseable coordinates are simply absent from the
   * response, not returned with placeholder lat/lng — nothing to filter
   * out here.
   */
  private hasLoadedFleetMap = false;

  private loadFleetMapPins(): void {
    if (this.hasLoadedFleetMap) return;
    this.hasLoadedFleetMap = true;
    this.isLoadingFleetMap.set(true);
    this.fleetMapService
      .getFleetMap()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.fleetMapPins.set([...res.data].sort((a, b) => a.label.localeCompare(b.label)));
          this.isLoadingFleetMap.set(false);
        },
        error: () => {
          this.hasLoadedFleetMap = false; // allow retry on next tab switch
          this.toastService.show(this.t('toast.fleetMapLoadFailed'), 'error');
          this.isLoadingFleetMap.set(false);
        },
      });
  }

  /** The single call that renders the grid — see class doc. `silent` skips
   *  the loading affordances (skeleton/dim) for background refreshes. */
  private load(silent = false): void {
    if (!silent) this.isLoading.set(true);
    this.fleetService
      .getFleetTiles(this.page(), this.size(), 'created,DESC')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.allTiles.set(res.data);
          this.page.set(res.page);
          this.size.set(res.size);
          this.totalElements.set(res.totalElements);
          this.totalPages.set(res.totalPages);
          this.isLoading.set(false);
        },
        error: () => {
          this.toastService.show(this.t('toast.loadFailed'), 'error');
          this.isLoading.set(false);
        },
      });
  }

  /** Fetches the rail's detail once per selection — see class doc. */
  private loadDetail(clientId: string): void {
    this.selectedTileDetail.set(null);
    this.isLoadingDetail.set(true);
    this.fleetService
      .getDeviceDetail(clientId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.selectedTileDetail.set(res.data);
          this.isLoadingDetail.set(false);
        },
        error: () => {
          this.toastService.show(this.t('toast.detailLoadFailed'), 'error');
          this.isLoadingDetail.set(false);
        },
      });
  }

  // ── View helpers ──────────────────────────────────────────────────────────

  /** Hero caption key — outcome-first copy that varies with the page's
   *  state (empty fleet / all clear / n need attention) instead of one
   *  grammar-bending catch-all sentence. */
  heroLineKey(): string {
    if (this.allTiles().length === 0) return 'hero.lineEmpty';
    const n = this.attentionCount();
    return n === 0 ? 'hero.lineAllClear' : n === 1 ? 'hero.lineOne' : 'hero.lineMany';
  }

  statusColor(status: FleetStatus): GridStatusColor {
    return STATUS_COLOR[status];
  }

  statusLabel(status: FleetStatus): string {
    return this.t(`status.${status}`);
  }

  /**
   * Tile / pill background tint for a status. Uses the registered `*-surface`
   * utilities (see the @theme block in styles.css) — not `surface-highlight-*`,
   * which is a raw token, not a generated Tailwind utility.
   */
  tintClass(status: FleetStatus): string {
    switch (STATUS_COLOR[status]) {
      case 'green':
        return 'bg-success-surface';
      case 'yellow':
        return 'bg-caution-surface';
      case 'orange':
        return 'bg-warning-surface';
      case 'red':
        return 'bg-danger-surface';
      default:
        return 'bg-sunken';
    }
  }

  /** Status dot background class — the 500-level icon-grade hues, same as
   *  LabelBadge's dot map (dots are marks, not text, so the darker
   *  text-tuned `success`/`caution` hues read wrong next to any badge).
   *  The two grey states stay distinguishable at dot size per the design:
   *  offline is a dark filled dot, shut down a hollow outlined one. */
  dotClass(status: FleetStatus): string {
    if (status === 'shutdown') return 'border-[1.5px] border-ink-3 bg-transparent';
    switch (STATUS_COLOR[status]) {
      case 'green':
        return 'bg-[var(--colorscales-green-500)]';
      case 'yellow':
        return 'bg-[var(--colorscales-yellow-500)]';
      case 'orange':
        return 'bg-[var(--colorscales-orange-500)]';
      case 'red':
        return 'bg-[var(--colorscales-red-500)]';
      default:
        return 'bg-ink-2';
    }
  }

  /** Status text/emphasis colour class. */
  textClass(status: FleetStatus): string {
    switch (STATUS_COLOR[status]) {
      case 'green':
        return 'text-success';
      case 'yellow':
        return 'text-caution';
      case 'orange':
        return 'text-warning';
      case 'red':
        return 'text-danger';
      default:
        return 'text-ink-2';
    }
  }

  /** Maps a fleet status to a LabelBadge dot colour. `null` (unknown status —
   *  see `selectedSummary`) renders with no dot, same as an inactive badge. */
  statusDot(status: FleetStatus | null): LabelBadgeDot | null {
    if (status === null) return null;
    switch (STATUS_COLOR[status]) {
      case 'green':
        return 'green';
      case 'yellow':
        return 'yellow';
      case 'orange':
        return 'orange';
      case 'red':
        return 'red';
      default:
        return null;
    }
  }

  /** Rail/legend status label, honest about an unresolved status. */
  statusLabelOrUnknown(status: FleetStatus | null): string {
    return status === null ? this.t('placeholders.dash') : this.statusLabel(status);
  }

  /** Tile meta line — battery, or the status word when there's no battery. */
  tileMeta(tile: FleetTile): string {
    return tile.battery != null ? `${tile.battery}%` : this.t('tile.noData');
  }

  /** Detailed card's meta-row status word ("Online") — shorter, outcome-first
   *  wording vs the rail's `status.*` labels. */
  tileStatusLabel(status: FleetStatus): string {
    return this.t(`tile.status.${status}`);
  }

  /** Coarse "2m ago" rendering for `lastSync` — see `relativeTime`'s doc
   *  comment for why this bypasses Transloco. */
  readonly relativeTime = relativeTime;

  /** Records a snapshot `<img>`'s real orientation once it loads — see
   *  `snapshotOrientation`'s doc comment for why this is detected from the
   *  pixels rather than bound to backend data. */
  onSnapshotLoad(event: Event, imageUrl: string): void {
    this.loadedSnapshots.update((set) => {
      if (set.has(imageUrl)) return set;
      return new Set(set).add(imageUrl);
    });
    const img = event.target as HTMLImageElement;
    if (!img.naturalWidth || !img.naturalHeight) return;
    const orientation: 'portrait' | 'landscape' = img.naturalWidth >= img.naturalHeight ? 'landscape' : 'portrait';
    this.snapshotOrientation.update((map) => {
      if (map.get(imageUrl) === orientation) return map;
      const next = new Map(map);
      next.set(imageUrl, orientation);
      return next;
    });
  }

  /** The browser couldn't fetch/decode a snapshot URL — show the failed
   *  frame instead of a broken image. */
  onSnapshotError(imageUrl: string): void {
    this.failedSnapshots.update((set) => {
      if (set.has(imageUrl)) return set;
      return new Set(set).add(imageUrl);
    });
  }

  /** True from first render of a snapshot URL until its load/error event —
   *  drives the image-slot spinner so a slow download never looks like a
   *  broken card. */
  snapshotFetching(imageUrl: string | null): boolean {
    return (
      imageUrl !== null &&
      !this.loadedSnapshots().has(imageUrl) &&
      !this.failedSnapshots().has(imageUrl)
    );
  }

  snapshotFailed(imageUrl: string | null): boolean {
    return imageUrl !== null && this.failedSnapshots().has(imageUrl);
  }

  /** CSS `aspect-ratio` for a snapshot frame — `3 / 4` portrait, `4 / 3`
   *  landscape/unknown (see `snapshotOrientation`'s doc comment on the
   *  unknown-until-loaded gap). */
  snapshotAspectRatio(imageUrl: string | null): string {
    const orientation = imageUrl ? this.snapshotOrientation().get(imageUrl) : undefined;
    return orientation === 'portrait' ? '3 / 4' : '4 / 3';
  }

  /** Orientation chip/spec label — `null` while unknown (nothing loaded yet). */
  snapshotOrientationLabel(imageUrl: string | null): string | null {
    const orientation = imageUrl ? this.snapshotOrientation().get(imageUrl) : undefined;
    if (!orientation) return null;
    return this.t(orientation === 'portrait' ? 'rail.orientationPortrait' : 'rail.orientationLandscape');
  }

  // ── Event handlers ──────────────────────────────────────────────────────────

  onTab(tab: MonitoringTab): void {
    this.tab.set(tab);
    this.selectedTileId.set(null);
    if (tab === 'map') this.loadFleetMapPins();
  }

  onStatusFilter(filter: FleetFilter): void {
    this.statusFilter.set(filter);
  }

  onModelChange(value: string | number): void {
    this.model.set(String(value));
  }

  selectTile(tile: FleetTile): void {
    const next = this.selectedTileId() === tile.clientId ? null : tile.clientId;
    this.selectedTileId.set(next);
    this.applySelection(next);
    if (next) this.loadDetail(next);
  }

  /** Closes the rail. Calls `applySelection` directly, right here — not via
   *  a reactive side-channel — so "close the drawer" and "deselect the pin
   *  on the map" are one action, not two things that happen to agree. */
  clearSelection(): void {
    this.selectedTileId.set(null);
    this.applySelection(null);
  }

  /** Double-click opens the display overview page (devices/:clientId). No
   *  device DTO to pass any more — the overview page fetches its own data
   *  by the clientId route param (see back-end gap #6, resolved). */
  openTile(tile: FleetTile): void {
    this.openDevice(tile.clientId);
  }

  /** The rail's "Open display" button — same destination as double-clicking
   *  a tile, but from whatever's currently selected (grid tile or map pin). */
  openSelectedDevice(): void {
    const clientId = this.selectedSummary()?.clientId;
    if (clientId) this.openDevice(clientId);
  }

  private openDevice(clientId: string): void {
    this.router.navigate(['..', 'devices', clientId], { relativeTo: this.route });
  }

  prevPage(): void {
    if (this.page() > 0) {
      this.page.update((p) => p - 1);
      this.selectedTileId.set(null);
      this.load();
    }
  }

  nextPage(): void {
    if (this.page() < this.totalPages() - 1) {
      this.page.update((p) => p + 1);
      this.selectedTileId.set(null);
      this.load();
    }
  }
}
